/**
 * Authentication state.
 *
 * Two invariants define this store, both required by ADR-0011 and the API's cookie strategy (ADR-0005):
 *
 * 1. The **access token is never persisted**. It lives in memory, so a stolen `localStorage` dump cannot mint API
 *    calls and an XSS payload cannot read a long-lived credential. A page reload re-acquires it through the
 *    `HttpOnly` refresh cookie, which the browser sends to the API but JavaScript can never read.
 * 2. The **refresh token is never stored here either** — only the access token and the profile are kept, and
 *    `isBootstrapped` records whether the silent refresh has already been attempted this page lifetime.
 */

import { create } from 'zustand';

import type { AuthSession, UserProfile } from '@/types/api';

interface AuthState {
  /** Bearer token for the current page lifetime; `null` when signed out or not yet restored. */
  accessToken: string | null;
  /** Moment at which {@link accessToken} stops being accepted, in epoch milliseconds. */
  accessTokenExpiresAt: number | null;
  /** Signed-in profile, including roles and entitlements. */
  user: UserProfile | null;
  /** True once the silent refresh has finished, whether or not it produced a session. */
  isBootstrapped: boolean;
  /** True while a sign-in, sign-out or refresh round-trip is in flight. */
  isBusy: boolean;
  /** Stores a freshly issued session and clears the busy flag. */
  setSession: (session: AuthSession) => void;
  /** Replaces the cached profile after a profile update (for example a new display name). */
  setUser: (user: UserProfile) => void;
  /** Marks the end of the initial silent-refresh attempt. */
  setBootstrapped: () => void;
  /** Toggles the busy flag. */
  setBusy: (busy: boolean) => void;
  /** Drops every credential in memory (sign-out, refresh failure, forced logout). */
  clear: () => void;
}

/** Converts the API's relative plus absolute expiry pair into an epoch-millisecond deadline. */
export function expiryFromSession(session: AuthSession): number {
  const fromAbsolute = Date.parse(session.accessTokenExpiresAtUtc);

  if (Number.isFinite(fromAbsolute)) {
    return fromAbsolute;
  }

  return Date.now() + session.expiresIn * 1000;
}

export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: null,
  accessTokenExpiresAt: null,
  user: null,
  isBootstrapped: false,
  isBusy: false,

  setSession: (session) =>
    set({
      accessToken: session.accessToken,
      accessTokenExpiresAt: expiryFromSession(session),
      user: session.user,
      isBootstrapped: true,
      isBusy: false,
    }),

  setUser: (user) => set({ user }),

  setBootstrapped: () => set({ isBootstrapped: true }),

  setBusy: (isBusy) => set({ isBusy }),

  clear: () => set({ accessToken: null, accessTokenExpiresAt: null, user: null, isBusy: false }),
}));

/** Selector: is there an authenticated session in memory right now? */
export function selectIsAuthenticated(state: AuthState): boolean {
  return state.accessToken !== null && state.user !== null;
}
